import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Play, Coins } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { getPlayer } from "@/lib/api";
import { formatDuration, formatDistance } from "@/lib/time";
import { flagFromCountry } from "@/lib/flags";
import { Button } from "@/components/ui/button";

export default function Profile() {
  const { player, updatePlayer, logout, loading } = usePlayer();
  const navigate = useNavigate();
  const [fresh, setFresh] = useState(player);

  useEffect(() => {
    if (loading) return;
    if (!player) { navigate("/"); return; }
    getPlayer(player.id).then((p) => { setFresh(p); updatePlayer(p); }).catch(() => {});
  }, [player, loading, navigate, updatePlayer]);

  if (!player) return null;
  const p = fresh || player;

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden" data-testid="profile-page">
      <div className="fixed inset-0 synth-grid opacity-25 pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 md:px-16 pt-16 pb-24">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-cyan-400 inline-flex items-center gap-2 transition-colors" data-testid="profile-back-link">
            <ArrowLeft className="w-3 h-3" /> Home
          </Link>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="profile-logout-button"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400 inline-flex items-center gap-2 transition-colors">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">/// DRIVER_PROFILE</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-6xl md:text-7xl text-white mt-2 inline-flex items-center gap-4" data-testid="profile-name">
            <span className="text-5xl md:text-6xl" aria-hidden="true" data-testid="profile-flag">{flagFromCountry(p.country)}</span>
            <span>{p.name}</span>
          </h1>
          <div className="font-mono text-sm text-zinc-500 mt-3 flex items-center gap-3 flex-wrap">
            <span>{p.email}</span> <span className="text-zinc-700">·</span>
            <span>Age {p.age}</span> <span className="text-zinc-700">·</span>
            <span>{p.country}</span> <span className="text-zinc-700">·</span>
            <span className="inline-flex items-center gap-2">
              Car
              <span
                className="inline-block w-4 h-4 rounded-sm ring-1 ring-white/15"
                style={{ background: p.car_color || "#06B6D4" }}
                data-testid="profile-car-color"
              />
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
          <BigStat span={2} label="Total Time Driven" value={formatDuration(p.total_time_seconds)} testid="profile-total-time" accent="cyan" />
          <BigStat span={1} label="High Score" value={(p.high_score || 0).toLocaleString()} testid="profile-high-score" accent="yellow" />
          <BigStat span={1} label="Distance Total" value={formatDistance(p.total_distance)} testid="profile-total-distance" accent="cyan" />
          <div className="border border-zinc-800 bg-black/60 backdrop-blur-xl p-6 md:col-span-4" data-testid="profile-coins">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-2">
              <Coins className="w-3 h-3 text-yellow-400" /> Coins Collected
            </div>
            <div className="font-display font-black text-4xl text-yellow-400 text-glow-yellow mt-3 tabular-nums">
              {(p.coins_collected || 0).toLocaleString()}
            </div>
          </div>
        </div>

        <div className="mt-12 flex gap-4">
          <Button onClick={() => navigate("/play")} data-testid="profile-play-button"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-widest h-12 px-6 border-glow-cyan">
            <Play className="w-4 h-4 mr-2" /> Continue driving
          </Button>
          <Button variant="outline" onClick={() => navigate("/leaderboard")} data-testid="profile-leaderboard-button"
            className="rounded-none border-zinc-800 bg-black/40 hover:bg-cyan-500/10 hover:text-cyan-300 font-mono text-xs uppercase tracking-widest h-12 px-6">
            View Leaderboard
          </Button>
        </div>

        <div className="mt-16 border border-zinc-800 bg-black/60 backdrop-blur-xl p-8" data-testid="profile-since">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Joined</div>
          <div className="font-mono text-sm text-white mt-2">{new Date(p.created_at).toLocaleString()}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-6">Last Seen</div>
          <div className="font-mono text-sm text-white mt-2">{new Date(p.last_seen_at).toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, testid, accent, span = 1 }) {
  const color = accent === "yellow" ? "text-yellow-400 text-glow-yellow" : "text-cyan-400 text-glow-cyan";
  return (
    <div className={`border border-zinc-800 bg-black/60 backdrop-blur-xl p-6 md:col-span-${span}`} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-4xl md:text-5xl ${color} mt-3 tabular-nums`}>{value}</div>
    </div>
  );
}
